import React from 'react';
import { Dialog, Box, IconButton, Typography } from '@mui/material';
import { Close, ChevronLeft, ChevronRight } from '@mui/icons-material';

/**
 * Lightbox
 * Full-screen image viewer. Mirrors LibraryDetailPage lightbox pattern.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string[]} images
 * @param {number} index
 * @param {(n: number) => void} onIndexChange
 * @param {string} alt
 */
function Lightbox({ open, onClose, images, index, onIndexChange, alt }) {
  const count = images.length;

  const handlePrev = () => {
    onIndexChange((index - 1 + count) % count);
  };

  const handleNext = () => {
    onIndexChange((index + 1) % count);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.95)',
          borderRadius: '0px',
          m: 0,
          maxHeight: '100vh',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '80vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Close */}
        <IconButton
          onClick={onClose}
          aria-label="Close lightbox"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: 'common.white',
            bgcolor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 2,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
          }}
        >
          <Close />
        </IconButton>

        {/* Prev */}
        {count > 1 && (
          <IconButton
            onClick={handlePrev}
            aria-label="Previous image"
            sx={{
              position: 'absolute',
              left: 24,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              zIndex: 2,
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {/* Image */}
        {images[index] && (
          <Box
            component="img"
            src={images[index]}
            alt={`${alt} ${index + 1}`}
            onError={(e) => {
              e.currentTarget.src =
                'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            }}
            loading="lazy"
            decoding="async"
            sx={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
          />
        )}

        {/* Next */}
        {count > 1 && (
          <IconButton
            onClick={handleNext}
            aria-label="Next image"
            sx={{
              position: 'absolute',
              right: 24,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
              zIndex: 2,
            }}
          >
            <ChevronRight />
          </IconButton>
        )}

        {/* Counter pill */}
        {count > 1 && (
          <Typography
            component="div"
            sx={{
              position: 'absolute',
              bottom: 24,
              left: '50%',
              transform: 'translateX(-50%)',
              color: 'common.white',
              bgcolor: 'rgba(0,0,0,0.5)',
              backdropFilter: 'blur(10px)',
              px: 2,
              py: 1,
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            {index + 1} / {count}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}

export default Lightbox;
